import keys from "Keys";
import { Mutex } from "async-mutex";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Define a service using a base URL and expected endpoints

const baseQuery = fetchBaseQuery({
  baseUrl: keys.baseUrl,
});

const tokenMutex = new Mutex(); // Mutex to ensure only one call to /getToken is made at a time
let tokenPromise; // Promise to hold the /getToken request

async function getToken(api, extraOptions) {
  try {
    return await baseQuery(
      {
        url: "/getToken",
        method: "GET",
        mode: "cors",
        credentials: "include",
      },
      api,
      extraOptions
    );
  } catch (error) {
    throw new Error("Failed to obtain token");
  }
}

// The access token is captured into `args.headers` when a query is DEFINED, so a request
// that queued behind an in-flight refresh can still be carrying the token that refresh just
// replaced. Re-stamp it from localStorage at send time.
function withCurrentToken(args) {
  const current = window.localStorage.getItem("token");
  if (!current || !args || typeof args !== "object" || !args.headers) return args;
  if (!("x-access-token" in args.headers)) return args;
  if (args.headers["x-access-token"] === current) return args;
  return { ...args, headers: { ...args.headers, "x-access-token": current } };
}

export default async function baseQueryWithReauth(args, api, extraOptions) {
  // Wait for any in-flight /getToken refresh to finish, THEN send with the current token.
  // This used to be `const mutex = new Mutex(); await mutex.waitForUnlock();` — a brand-new
  // mutex per call, which is always unlocked, so it was a complete no-op. Every request in
  // flight during a refresh therefore stampeded with the STALE token, 403'd, and kicked off
  // its own extra refresh — and because the rt ROTATES on every refresh, those extra
  // refreshes raced and lost.
  await tokenMutex.waitForUnlock();

  let result = await baseQuery(withCurrentToken(args), api, extraOptions);
  if (
    result?.error?.status === 400 && result?.error?.data?.message === "service not found"
  ) {
    tokenPromise = false
    window.localStorage.removeItem("metaData");
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("user");
    window.location.href = "/auth/login"; // Redirect to the login page
    return false;
  }
  // Delegated-admin scope denial (S2) is an AUTHORIZATION failure, not an expired
  // token. Return it straight to the caller: do NOT refresh (a refresh churns the rt
  // and hard-logs-out Safari users on a 403). The component renders a clean denied state.
  if (result?.error?.status === 403 && result?.error?.data?.errorCode === "SCOPE_DENIED") {
    return result;
  }
  if (
    result.error &&
    (result.error.status === 403 || result.error.status === 401 || result?.error?.data?.errorCode === 401)
  ) {
    if (
      result?.error?.status === 401 || result?.error?.data?.errorCode === 401
    ) {
      tokenPromise = false
      window.localStorage.removeItem("metaData");
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("user");
      window.location.href = "/auth/login"; // Redirect to the login page
      /* setTimeout(() => {
        window.location.reload();
      }, 1000); */
      return false;
    }
    try {
      await tokenMutex.runExclusive(async () => {
        // SINGLE-FLIGHT: another request may have refreshed while we queued for this mutex.
        // The rt is ROTATED on every successful /getToken (old row -> valid:false, new rt
        // issued), so refreshing again here would race its own predecessor and LOSE — the
        // loser gets 401/403, which is terminal, and ejects a user whose session is fine.
        // Observed in prod: two /getToken calls in the same second across the two authMS
        // replicas, one 200 and one 401. If the stored token already changed, that refresh
        // succeeded — just retry with its token instead of minting another one.
        const sentToken = args?.headers?.["x-access-token"];
        const currentToken = window.localStorage.getItem("token");
        if (currentToken && sentToken && currentToken !== sentToken) {
          result = await baseQuery(
            {
              ...args,
              headers: { ...args.headers, "x-access-token": currentToken },
            },
            api,
            extraOptions
          );
          return;
        }
        if (!tokenPromise) {
          tokenPromise = getToken(api, extraOptions);
        }
        const refreshResult = await tokenPromise;
        tokenPromise = false;
        if (refreshResult.data?.payload?.token) {
          window.localStorage.setItem(
            "token",
            refreshResult.data.payload.token
          );
          result = await baseQuery(
            {
              // Spread `args`. This used to pass ONLY { url, headers }, which dropped
              // `method`, `body` and `params` — so fetchBaseQuery fell back to its default,
              // GET. Any MUTATION retried after a token refresh (e.g. PUT
              // devices/updateMeta/:id, which sits behind verifyApiToken) was silently
              // re-issued as a GET: the server answered 200, the UI showed success, and the
              // write simply never happened. Spreading args preserves the original request.
              ...args,
              headers: {
                ...args.headers,
                "x-access-token": refreshResult.data.payload.token,
              },
            },
            api,
            extraOptions
          );
        } else {
          // Tear the session down ONLY when /getToken itself says the session is dead:
          // a 4xx (expired/invalid/missing rt — incl. the joi 400 for a missing cookie),
          // a body errorCode 401, or a malformed 2xx without payload.token. A 5xx or a
          // network failure during refresh is an INFRASTRUCTURE blip (authMS now returns
          // 503 for those) — logging out on it would eject every user whose token happened
          // to roll over during a database wobble. Keep the session, leave the original
          // 403 result as the error, and let a later request retry the refresh.
          const refreshStatus = refreshResult.error?.status;
          const terminal =
            refreshResult.data || // 2xx without payload.token
            refreshStatus === 400 ||
            refreshStatus === 401 ||
            refreshStatus === 403 ||
            refreshResult.error?.data?.errorCode === 401;
          if (terminal) {
            window.localStorage.removeItem("metaData");
            window.localStorage.removeItem("token");
            window.localStorage.removeItem("user");
            window.location.href = "/auth/login"; // Redirect to the login page
            /* setTimeout(() => {
              window.location.reload();
            }, 1000); */
          } else {
            console.error(
              "Token refresh failed transiently; keeping session",
              refreshResult.error
            );
          }
        }
      });
    } catch (error) {
      console.error("Error obtaining token:", error);
    }
  }
  return result;
}
